import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useChat } from "../lib/chat";
import { useT, lbl } from "../lib/i18n";
import { toast } from "sonner";
import { Send, UserCheck, UserPlus, XCircle, Volume2, VolumeX, Bell } from "lucide-react";
import { FaqManager, CannedManager } from "../components/ChatManagers";
import { ConvertChatModal } from "../components/ConvertChatModal";

import { tr } from "../lib/i18n";
const TABS = [
  { k: "conversations", l: tr("x.conversations") },
  { k: "faq", l: tr("x.faq") },
  { k: "canned", l: tr("x.canned_replies") },
];
const SCOPES = ["unassigned", "mine", "all", "closed"];

export default function LiveChatPage() {
  const { user, can } = useAuth();
  const chat = useChat();
  const { t } = useT();
  const [tab, setTab] = useState("conversations");
  const [converting, setConverting] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [composer, setComposer] = useState("");
  const [scope, setScope] = useState("unassigned");
  const [canned, setCanned] = useState([]);
  const [users, setUsers] = useState([]);
  const scrollRef = useRef(null);

  const selected = thread?.conversation || conversations.find((c) => c.id === selectedId);

  const loadList = async () => {
    try {
      const { data } = await api.get("/chat/conversations", { params: { scope, user_id: user?.id } });
      setConversations(data?.items || []);
    } catch { /* list is refreshed again on the next event */ }
  };
  const loadThread = async (cid) => {
    try {
      const { data } = await api.get(`/chat/conversations/${cid}`);
      setThread(data);
      setTimeout(() => scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }), 50);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const refresh = () => { loadList(); if (selectedId) loadThread(selectedId); chat?.refreshCounts?.(); };

  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [scope]);
  useEffect(() => { if (selectedId) loadThread(selectedId); }, [selectedId]);
  useEffect(() => {
    api.get("/chat/canned").then(({ data }) => setCanned(data || [])).catch(() => {});
    if (can("users", "view")) api.get("/users").then(({ data }) => setUsers(Array.isArray(data) ? data : data?.items || [])).catch(() => {});
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const e = chat?.lastEvent;
    if (!e) return;
    if (e.conversation_id && e.conversation_id === selectedId) loadThread(selectedId);
    loadList();
    // eslint-disable-next-line
  }, [chat?.lastEvent]);

  const act = (fn, msg) => async (...args) => {
    try { await fn(...args); if (msg) toast.success(msg); refresh(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const claim = act(() => api.post(`/chat/conversations/${selectedId}/claim`), tr("x.assigned_to_you"));
  const transfer = act((uid) => api.post(`/chat/conversations/${selectedId}/transfer`, { user_id: uid }), tr("x.conversation_transferred"));
  const closeConv = act((outcome) => api.post(`/chat/conversations/${selectedId}/close`, { outcome }), tr("x.conversation_closed"));
  const close = () => {
    const outcome = window.prompt(tr("x.outcome_e_g_converted_no_answer_not_inte"), "closed");
    if (outcome != null) closeConv(outcome);
  };
  const convert = () => setConverting(true);
  const send = async () => {
    const text = composer.trim();
    if (!text || !selectedId) return;
    setComposer("");
    try { await api.post(`/chat/conversations/${selectedId}/send`, { text }); loadThread(selectedId); }
    catch (e) { toast.error(formatApiError(e)); setComposer(text); }
  };

  const isClosed = selected?.status === "closed";
  const others = useMemo(() => users.filter((u) => u.id !== user?.id && u.is_active !== false), [users, user]);

  return (
    <div data-testid="livechat-page">
      {converting && selected && <ConvertChatModal conv={selected} onClose={() => setConverting(false)} onSaved={() => { setConverting(false); refresh(); }} />}
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="kicker">{tr("x.visitor_engagement")}</div>
          <h1 className="mt-2" style={{ fontSize: 34, fontWeight: 600, letterSpacing: "-0.02em" }}>{t("livechat.title")}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button className="btn-secondary" onClick={() => chat?.toggleSound?.()} data-testid="chat-sound-toggle">
            {chat?.soundOn ? <><Volume2 size={14} /> {tr("x.sound_on")}</> : <><VolumeX size={14} /> {tr("x.sound_off")}</>}
          </button>
          <button className="btn-secondary" onClick={() => chat?.requestDesktopNotifs?.()} data-testid="chat-desktop-notifs"><Bell size={14} /> {tr("x.desktop_alerts")}</button>
          <span className="text-xs px-3 py-2" data-testid="chat-socket-status"
            style={{ border: "1px solid var(--divider)", color: chat?.socketReady ? "var(--st-available)" : "var(--st-reserved)" }}>
            {chat?.socketReady ? tr("x.chat_connected") : tr("x.reconnecting")}
          </span>
        </div>
      </div>

      <div className="underline-tabs mb-4">
        {TABS.map((x) => (
          <button key={x.k} className={tab === x.k ? "active" : ""} onClick={() => setTab(x.k)} data-testid={`chat-tab-${x.k}`}>{x.l}</button>
        ))}
      </div>

      {tab === "faq" && <FaqManager />}
      {tab === "canned" && <CannedManager />}

      {tab === "conversations" && (
        <div className="grid gap-[2px] livechat-grid"
          style={{ background: "var(--divider)", border: "1px solid var(--divider)", gridTemplateColumns: "minmax(300px,1fr) minmax(0,2fr)", height: "max(560px, calc(100vh - 300px))" }}>
          <aside style={{ background: "#fff", display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 }}>
            <div className="livechat-scopes" data-testid="chat-scopes">
              {SCOPES.map((s) => (
                <button key={s} className={scope === s ? "active" : ""} onClick={() => setScope(s)} data-testid={`chat-scope-${s}`}>
                  {t(`livechat.scope_${s /* i18n-keys: unassigned,mine,all,closed */}`)}
                  {s === "unassigned" && chat?.counts?.unassigned > 0 ? ` (${chat.counts.unassigned})` : ""}
                  {s === "mine" && chat?.counts?.mine > 0 ? ` (${chat.counts.mine})` : ""}
                </button>
              ))}
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {conversations.length === 0 && <div className="p-6 text-sm opacity-60">{tr("x.no_conversations_in_this_view")}</div>}
              {conversations.map((c) => {
                const active = c.id === selectedId;
                return (
                  <button key={c.id} onClick={() => setSelectedId(c.id)} className="w-full text-start" data-testid={`conv-${c.id}`}
                    style={{ padding: "14px 16px", display: "block", borderBottom: "1px solid var(--divider-soft)",
                      background: active ? "var(--accent-100)" : "transparent",
                      borderInlineStart: active ? "3px solid var(--accent)" : "3px solid transparent" }}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium truncate flex-1">{c.name || tr("x.visitor", { p0: c.visitor_id?.slice(0, 6) || "" })}</span>
                      {c.unread_for_agent > 0 && <span className="text-[10px] px-1.5" style={{ background: "var(--p)", color: "var(--p-ink)" }}>{c.unread_for_agent}</span>}
                      {c.status === "unassigned" && <span className="tag tag-consign" style={{ textTransform: "none" }}>{t("livechat.unassigned")}</span>}
                    </div>
                    <div className="text-xs opacity-70 truncate">{c.vehicle_label || tr("x.general_enquiry")}</div>
                    <div className="text-[10px] mt-1 opacity-60">{c.status !== "unassigned" ? `${lbl(c.status)} · ` : ""}<bdi dir="ltr">{c.last_message_at?.slice(11, 16)}</bdi></div>
                  </button>
                );
              })}
            </div>
          </aside>

          <section style={{ background: "#fff", display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 }}>
            <header className="flex items-center justify-between gap-3 px-5 py-4 flex-wrap" style={{ borderBottom: "1px solid var(--divider-soft)" }}>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate" data-testid="chat-thread-title">{selected ? (selected.name || tr("x.visitor_2")) : tr("x.select_a_conversation")}</div>
                <div className="text-xs opacity-60">{selected ? `${lbl(selected.status)} · ${selected.email || selected.phone || tr("x.no_contact_info")}` : "—"}</div>
              </div>
              {selected && (
                <div className="flex gap-2 flex-wrap">
                  {selected.status === "unassigned" && can("chat", "edit") && (
                    <button className="btn-primary" onClick={claim} data-testid="assign-to-me"><UserCheck size={14} strokeWidth={1.75} /> {t("livechat.assign_me")}</button>
                  )}
                  {selected.status === "assigned" && can("chat", "edit") && (
                    <>
                      <select className="input" style={{ width: 170 }} value="" onChange={(e) => e.target.value && transfer(e.target.value)} data-testid="chat-transfer">
                        <option value="">{tr("x.transfer_to")}</option>
                        {others.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                      </select>
                      <button className="btn-secondary" onClick={close} data-testid="close-btn"><XCircle size={14} strokeWidth={1.75} /> {tr("x.close")}</button>
                    </>
                  )}
                </div>
              )}
            </header>

            <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 20, background: "var(--ground)", minHeight: 320 }} data-testid="chat-thread">
              {(thread?.messages || []).map((m) => {
                if (m.sender_type === "system") return <div key={m.id} className="text-center text-xs italic opacity-60 my-2">{m.text}</div>;
                const isAgent = m.sender_type === "agent";
                return (
                  <div key={m.id} className="mb-3 flex" style={{ justifyContent: isAgent ? "flex-end" : "flex-start" }}>
                    <div style={{ maxWidth: "72%", padding: "10px 14px", fontSize: 14, lineHeight: 1.5,
                      background: isAgent ? "var(--p)" : "#fff", color: isAgent ? "var(--p-ink)" : "var(--text)",
                      border: isAgent ? 0 : "1px solid var(--divider)" }}>
                      <div className="text-[10px] opacity-70 mb-0.5">{m.sender_name}</div>
                      <div className="whitespace-pre-wrap">{m.text}</div>
                    </div>
                  </div>
                );
              })}
              {!thread && <div className="opacity-60 text-sm">{tr("x.select_a_conversation_to_see_the_thread")}</div>}
            </div>

            {can("chat", "edit") ? (
            <footer style={{ borderTop: "1px solid var(--divider-soft)", padding: 12 }}>
              {canned.length > 0 && (
                <div className="flex gap-1.5 flex-wrap mb-2">
                  {canned.map((c) => (
                    <button key={c.id} className="btn-ghost text-xs" style={{ border: "1px solid var(--divider)" }} title={c.text}
                      onClick={() => setComposer((v) => (v ? v + " " : "") + c.text)} data-testid={`canned-${c.id}`}>{c.title}</button>
                  ))}
                </div>
              )}
              <div className="flex gap-2 items-stretch">
                <textarea value={composer} onChange={(e) => setComposer(e.target.value)} disabled={!selected || isClosed}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder={t("livechat.composer_ph")} rows={2}
                  className="input" style={{ height: "auto", padding: 10, resize: "none", flex: 1 }} data-testid="chat-composer" />
                <button className="btn-primary" onClick={send} disabled={!selected || isClosed || !composer.trim()} data-testid="chat-send">
                  <Send size={14} strokeWidth={1.75} /> {t("livechat.send")}
                </button>
              </div>
            </footer>
            ) : (
              <footer className="text-xs opacity-60" style={{ borderTop: "1px solid var(--divider-soft)", padding: 12 }} data-testid="chat-view-only">{tr("x.view_only")}</footer>
            )}
          </section>
        </div>
      )}

      {tab === "conversations" && selected && (
            <div style={{ background: "#fff", padding: 24, border: "1px solid var(--divider)", borderTop: 0 }} data-testid="chat-visitor-context">
              <div className="kicker mb-3">{tr("x.visitor_context")}</div>
              <div className="grid gap-5" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))" }}>
                <KV label={tr("x.name")} value={selected.name || tr("x.anonymous")} />
                <KV label={tr("x.contact")} value={selected.phone || selected.email || "—"} />
                <KV label={tr("x.vehicle_2")} value={selected.vehicle_label || tr("x.not_linked")} />
                <KV label={tr("x.started")} value={selected.created_at?.slice(0, 16).replace("T", " ") || "—"} />
                <KV label={tr("x.outcome")} value={selected.outcome || "—"} />
                {can("chat", "edit") && <div className="flex items-end">
                  <button className="btn-primary w-full justify-center" onClick={convert} data-testid="save-as-customer">
                    <UserPlus size={14} strokeWidth={1.75} /> {t("livechat.save_customer")}
                  </button>
                </div>}
              </div>
            </div>
      )}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="kpi-label">{label}</div>
      <div className="mt-1 text-sm font-medium truncate">{value}</div>
    </div>
  );
}
